import React, { useState, useEffect } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import { CompanyLogoUploader } from './CompanyLogoUploader';
import { LogoPlacementEditor } from './LogoPlacementEditor';
import { resolveLogoPlacement, LogoPlacement } from '../utils/logoPlacement';

interface BrandingData {
  logoUrl?: string | null;
  logoS3Key?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  logoWidth?: number | null;
  logoX?: number | null;
  logoY?: number | null;
  logoOnAllPages?: boolean;
  companyDisplayName?: string | null;
  headerText?: string | null;
  footerText?: string | null;
}

interface CompanyBrandingSectionProps {
  initialBranding?: BrandingData;
  onBrandingUpdate?: (branding: BrandingData) => void;
  token?: string | null;
}

export const CompanyBrandingSection: React.FC<CompanyBrandingSectionProps> = ({
  initialBranding,
  onBrandingUpdate,
  token
}) => {
  const [branding, setBranding] = useState<BrandingData>(initialBranding || {
    logoPosition: 'right',
    logoOnAllPages: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (initialBranding) {
      setBranding(initialBranding);
    }
  }, [initialBranding]);

  // Fetch the logo as a data URL so the placement editor can render it
  // (mirrors CompanyLogoUploader's fetch of the auth-protected stream endpoint).
  useEffect(() => {
    const url = branding.logoUrl;
    if (!url) {
      setLogoDataUrl(null);
      return;
    }
    if (url.startsWith('data:')) {
      setLogoDataUrl(url);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const full = url.startsWith('http') ? url : `${apiUrl}${url}`;
        const response = await fetch(full, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled) setLogoDataUrl(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to fetch logo for placement editor:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branding.logoUrl, token]);

  const placement = resolveLogoPlacement(branding);
  const handlePlacementChange = (p: LogoPlacement) => {
    setBranding((prev) => ({ ...prev, logoWidth: p.width, logoX: p.x, logoY: p.y }));
  };

  const handleLogoUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const response = await fetch(`${apiUrl}/api/admin/company/branding/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to upload logo');
    }

    const data = await response.json();
    setBranding(data.branding);
    if (onBrandingUpdate) {
      onBrandingUpdate(data.branding);
    }
  };

  const handleLogoDelete = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const response = await fetch(`${apiUrl}/api/admin/company/branding/logo`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete logo');
    }

    const data = await response.json();
    setBranding(data.branding);
    if (onBrandingUpdate) {
      onBrandingUpdate(data.branding);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/admin/company/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          logoPosition: branding.logoPosition,
          logoOnAllPages: branding.logoOnAllPages,
          logoWidth: branding.logoWidth,
          logoX: branding.logoX,
          logoY: branding.logoY,
          companyDisplayName: branding.companyDisplayName,
          headerText: branding.headerText,
          footerText: branding.footerText
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update branding');
      }

      const data = await response.json();
      setBranding(data.branding);
      if (onBrandingUpdate) {
        onBrandingUpdate(data.branding);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save branding settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Company Branding
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Customize your company logo and branding that appears on exported PDF reports
        </p>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Company Logo
        </label>
        <CompanyLogoUploader
          currentLogoUrl={branding.logoUrl}
          onUpload={handleLogoUpload}
          onDelete={branding.logoUrl ? handleLogoDelete : undefined}
          token={token}
        />
      </div>

      {/* Logo Placement (drag + resize preview) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Logo Placement &amp; Document Preview
        </label>
        <LogoPlacementEditor
          logoDataUrl={logoDataUrl}
          placement={placement}
          onChange={handlePlacementChange}
          companyName={branding.companyDisplayName || undefined}
          headerText={branding.headerText || undefined}
        />
      </div>

      {/* Logo on All Pages */}
      <div>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={branding.logoOnAllPages}
            onChange={(e) => setBranding({ ...branding, logoOnAllPages: e.target.checked })}
            className="mr-2 text-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Show logo on all pages
          </span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
          If unchecked, logo will only appear on the first page
        </p>
      </div>

      {/* Company Display Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Company Display Name (Optional)
        </label>
        <input
          type="text"
          value={branding.companyDisplayName || ''}
          onChange={(e) => setBranding({ ...branding, companyDisplayName: e.target.value })}
          placeholder="Your Company Name"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Override the company name displayed in PDF headers/footers
        </p>
      </div>

      {/* Custom Header Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Custom Header Text (Optional)
        </label>
        <input
          type="text"
          value={branding.headerText || ''}
          onChange={(e) => setBranding({ ...branding, headerText: e.target.value })}
          placeholder="e.g., Confidential Report"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      {/* Custom Footer Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Custom Footer Text (Optional)
        </label>
        <input
          type="text"
          value={branding.footerText || ''}
          onChange={(e) => setBranding({ ...branding, footerText: e.target.value })}
          placeholder="e.g., © 2025 Your Company. All rights reserved."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save Branding Settings'}
        </button>

        {error && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <span className="text-sm text-green-600 dark:text-green-400">
            Settings saved successfully!
          </span>
        )}
      </div>
    </div>
  );
};
